// Billing, export and cancellation. Admin only (spec: 09-onboarding-billing.md).
//
// The whole screen is written to the same rule: SAY THE WHOLE THING, WITH
// DATES. Spec 09 asks for "every step stated plainly on the cancel screen. No
// retention calls, no forms", and the positioning in CLAUDE.md is that easy
// exit is a headline feature rather than something buried. So the cancel panel
// spells out the three dates before anyone clicks, the export panel says how
// long the file lasts, and the estimate for the month in progress is labelled
// as an estimate rather than presented as a bill.
//
// Nothing here does money arithmetic. The server sends cents and this file
// formats them — a subtotal recomputed in the browser is a subtotal that can
// disagree with the invoice.
import { Fragment, useCallback, useEffect, useState } from 'react';
import { ApiError, api, type Tenant } from '../api';
import {
  billing,
  fileSize,
  longDate,
  money,
  monthName,
  type BillingOverview,
  type ExportOverview,
} from '../billingApi';
import { useSession } from '../session';
import { Banner, Field } from '../ui';
import { StatusBadge } from '../components/ui/StatusBadge';
import { CardList, RowCard } from '../components/ui/RowCard';
import { SampleDataPanel } from '../components/SampleDataPanel';
import { DownloadLink } from '../components/DownloadLink';

export function BillingPage() {
  const { user, tenant, refresh: refreshSession } = useSession();
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [exports, setExports] = useState<ExportOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const load = useCallback(async () => {
    try {
      const [next, files] = await Promise.all([billing.overview(), billing.exports()]);
      setOverview(next);
      setExports(files);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load your billing details.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Stripe sends the admin back here with the session id on the URL. Reading it
  // and immediately clearing it keeps a refresh from re-confirming a card.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('setup_session') ?? params.get('fake_setup');
    if (!sessionId) return;

    window.history.replaceState({}, '', window.location.pathname);
    void (async () => {
      try {
        await billing.confirmCard(sessionId);
        setNotice('Card saved. Thank you.');
        await load();
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That card could not be saved.');
      }
    })();
  }, [load]);

  async function run(action: () => Promise<unknown>, done: string): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(done);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function addCard(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { url } = await billing.startCardSetup();
      // Card capture happens on the provider's own page; this app never sees a
      // card number (spec 09).
      window.location.href = url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not open the card form.');
      setBusy(false);
    }
  }

  if (!user) return null;
  if (!overview) {
    return (
      <section>
        <h1>Billing</h1>
        {error && <Banner kind="error">{error}</Banner>}
        {!error && <p className="hint">Loading…</p>}
      </section>
    );
  }

  const { nextBill } = overview;

  return (
    <section>
      <h1>Billing</h1>

      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="ok">{notice}</Banner>}

      <StatusPanel overview={overview} />

      {/* --- The card ------------------------------------------------- */}
      <section className="panel">
        <h2>Payment</h2>
        {overview.card ? (
          <>
            <p>
              {overview.card.brand} ending {overview.card.last4}, expires{' '}
              {String(overview.card.expMonth).padStart(2, '0')}/{overview.card.expYear}
            </p>
            <div className="row">
              <button type="button" onClick={addCard} disabled={busy}>
                Replace card
              </button>
              <button
                type="button"
                className="secondary"
                disabled={busy}
                onClick={() => void run(billing.removeCard, 'Card removed.')}
              >
                Remove card
              </button>
            </div>
          </>
        ) : (
          <>
            <p>No card on file.</p>
            <button type="button" onClick={addCard} disabled={busy}>
              Add a card
            </button>
          </>
        )}
        <p className="hint">
          Card details are entered on our payment provider&rsquo;s own page and never touch
          Dayboard&rsquo;s servers.
        </p>
      </section>

      {/* --- Details that appear on tax documents ---------------------- */}
      <AbnPanel tenant={tenant} onSaved={refreshSession} />

      {/* --- The month in progress ------------------------------------ */}
      <section className="panel">
        <h2>{monthName(nextBill.month)} so far</h2>
        {nextBill.seats.length === 0 ? (
          <p>Nobody is using a paid seat this month.</p>
        ) : (
          <table className="data-table ledger">
            <tbody>
              {nextBill.seats.map((seat) => (
                <tr key={seat.role}>
                  <td>
                    {seat.label} × {seat.quantity}
                  </td>
                  <td className="amount">{money(seat.amountCents)}</td>
                </tr>
              ))}
              {nextBill.discountCents > 0 && (
                <tr>
                  <td>
                    {overview.foundingMember
                      ? `Founding member, ${overview.discountPercent}% off for life`
                      : `Discount (${overview.discountPercent}%)`}
                  </td>
                  <td className="amount">−{money(nextBill.discountCents)}</td>
                </tr>
              )}
              <tr className="total">
                <td>Estimated total</td>
                <td className="amount">{money(nextBill.totalCents)}</td>
              </tr>
              {/* Prices are GST-inclusive (2026-09-04): the GST is inside the
                  total, so it reads as a disclosure below it, never as a row
                  that adds to it. */}
              {nextBill.gstCents > 0 && (
                <tr>
                  <td>Includes GST of</td>
                  <td className="amount">{money(nextBill.gstCents)}</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        <p className="hint">
          An estimate, not a bill. The month is not over; you are charged at the start of next
          month for whoever was active during this one, and nobody is charged for an invitation
          they never accepted.
        </p>
      </section>

      {/* --- History --------------------------------------------------- */}
      <section className="panel">
        <h2>Invoices</h2>
        {overview.invoices.length === 0 ? (
          <p>No invoices yet.</p>
        ) : (
          <>
          <CardList>
            {overview.invoices.map((invoice) => (
              <RowCard
                key={invoice.id}
                title={<span className="tabular-nums">{invoice.number ?? 'Draft'}</span>}
                badges={<StatusBadge status={invoice.status} kind="invoice" />}
              >
                <div>
                  {monthName(invoice.period)} · {money(invoice.totalCents)}
                  {invoice.number && (
                    <>
                      {' · '}
                      {/* A plain same-origin link: the download rides the admin
                          session cookie, never a bearer URL (the export rule). */}
                      <DownloadLink href={`/api/billing/invoices/${invoice.id}/pdf`}>PDF</DownloadLink>
                    </>
                  )}
                </div>
                {invoice.creditNotes.map((note) => (
                  <div key={note.id} className="table-muted">
                    {note.number} · −{money(note.totalCents)} refunded ·{' '}
                    <DownloadLink href={`/api/billing/credit-notes/${note.id}/pdf`}>PDF</DownloadLink>
                  </div>
                ))}
              </RowCard>
            ))}
          </CardList>
          <table className="data-table ledger max-sm:hidden">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Period</th>
                <th>Status</th>
                <th className="amount">Total</th>
                <th>
                  <span className="sr-only">Document</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {overview.invoices.map((invoice) => (
                // A credit note is its own document, so it gets its own row —
                // but indented under the invoice it adjusts, because that is
                // the only way to read it. The invoice's own figures are never
                // altered here; the note sits beside them.
                <Fragment key={invoice.id}>
                  <tr>
                    <td className="font-medium tabular-nums">{invoice.number ?? '—'}</td>
                    <td className="table-muted whitespace-nowrap">{monthName(invoice.period)}</td>
                    <td>
                      <StatusBadge status={invoice.status} kind="invoice" />
                    </td>
                    <td className="amount">{money(invoice.totalCents)}</td>
                    <td>
                      {invoice.number && (
                        // Same-origin link riding the admin session cookie —
                        // never a bearer URL (the export rule).
                        <DownloadLink href={`/api/billing/invoices/${invoice.id}/pdf`}>PDF</DownloadLink>
                      )}
                    </td>
                  </tr>
                  {invoice.creditNotes.map((note) => (
                    <tr key={note.id} className="table-muted">
                      <td className="tabular-nums pl-6">{note.number}</td>
                      <td className="whitespace-nowrap">{longDate(note.issuedAt)}</td>
                      <td>{note.reason}</td>
                      <td className="amount">−{money(note.totalCents)}</td>
                      <td>
                        <DownloadLink href={`/api/billing/credit-notes/${note.id}/pdf`}>PDF</DownloadLink>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
          {overview.invoices.some((invoice) => invoice.creditNotes.length > 0) && (
            <p className="hint">
              A CN- line is an adjustment note: the paperwork for a refund. The tax invoice
              above it is left exactly as it was issued, because that is what it was; the
              adjustment note is what offsets it. Both were emailed to you.
            </p>
          )}
          </>
        )}
      </section>

      <ExportPanel
        exports={exports}
        busy={busy}
        onRequest={() =>
          void run(
            billing.requestExport,
            'Building your export. We will email you when it is ready.'
          )
        }
      />

      <SampleDataPanel />

      {/* --- Cancel ---------------------------------------------------- */}
      <section className="panel danger">
        <h2>{overview.cancelledAt ? 'Cancelled' : 'Cancel'}</h2>

        {overview.cancelledAt ? (
          <>
            <p>
              Cancelled on {longDate(overview.cancelledAt)}. Nothing more will be charged.
            </p>
            <Timetable overview={overview} />
            <button
              type="button"
              disabled={busy}
              onClick={() => void run(billing.reactivate, 'Welcome back. Everything is live again.')}
            >
              Reactivate
            </button>
            {!overview.card && (
              <p className="hint">Add a card above first, and the account comes straight back.</p>
            )}
          </>
        ) : confirmingCancel ? (
          <>
            <p>Here is exactly what happens, so there are no surprises:</p>
            <Timetable overview={overview} projected />
            <div className="row">
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() => {
                  setConfirmingCancel(false);
                  void run(billing.cancel, 'Your subscription is cancelled.');
                }}
              >
                Yes, cancel
              </button>
              <button type="button" className="secondary" onClick={() => setConfirmingCancel(false)}>
                Keep my subscription
              </button>
            </div>
          </>
        ) : (
          <>
            <p>
              Cancel any time, from here, in two clicks. No phone call, no form, no notice period;
              and you can export everything on your way out.
            </p>
            <button type="button" className="secondary" onClick={() => setConfirmingCancel(true)}>
              Cancel subscription
            </button>
          </>
        )}
      </section>

      {/* Billing is where the terms actually bind — prices, arrears,
          cancellation — so the agreement is one click away from it. */}
      <p className="hint">
        Billing works as described in the{' '}
        <a href="https://dayboard.com.au/terms.html" target="_blank" rel="noreferrer">
          terms of service
        </a>
        .
      </p>
    </section>
  );
}

/**
 * The three dates a cancellation involves. Shown before the customer commits
 * and again afterwards, because "when exactly does my data go?" is the question
 * this screen exists to answer without anyone having to ask it.
 */
/**
 * The customer's own ABN — shown here because this is where their tax documents
 * live, and editable here because it is optional at signup (Nathan, round 19).
 * An optional field that can only ever be set once is a trap: the value is
 * printed on every invoice and adjustment note, so a skipped or mistyped one
 * needs a way back without asking us to edit the database.
 */
function AbnPanel({ tenant, onSaved }: { tenant: Tenant | null; onSaved: () => Promise<void> }) {
  const [value, setValue] = useState(tenant?.abn ?? '');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The per-FIELD message, separate from the banner. Without it a rejected ABN
  // says only "some of those details need fixing" on a panel with one field in
  // it, which tells the reader nothing they did not already know.
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [saved, setSaved] = useState(false);

  if (!tenant) return null;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setFieldError(undefined);
    try {
      await api.updateCompany({ abn: value });
      await onSaved();
      setEditing(false);
      setSaved(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
        setFieldError(err.fields.abn);
      } else {
        setError('Could not save that. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Your ABN</h2>
      {editing ? (
        <form onSubmit={save} noValidate>
          {error && <Banner kind="error">{error}</Banner>}
          <Field
            label="ABN"
            name="abn"
            value={value}
            onChange={setValue}
            error={fieldError}
            required={false}
            hint="Leave it blank if you would rather not give one."
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="numeric"
          />
          <div className="row">
            <button type="submit" disabled={busy}>
              Save
            </button>
            <button
              type="button"
              className="secondary"
              disabled={busy}
              onClick={() => {
                setValue(tenant.abn ?? '');
                setError(null);
                setFieldError(undefined);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <>
          <p>{tenant.abn ?? 'Not given.'}</p>
          {saved && <p className="hint">Saved.</p>}
          <button type="button" onClick={() => setEditing(true)}>
            {tenant.abn ? 'Change' : 'Add your ABN'}
          </button>
          <p className="hint">
            Shown on your tax invoices and on any adjustment note, so your bookkeeper can match
            them up. Optional; Dayboard does not need it.
          </p>
        </>
      )}
    </section>
  );
}

function Timetable({
  overview,
  projected = false,
}: {
  overview: BillingOverview;
  projected?: boolean;
}) {
  // Before cancelling, the server has not computed the dates yet. The end of
  // this month and sixty days after it is exactly what it will decide.
  const now = new Date();
  const endOfMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const liveUntil = projected
    ? endOfMonth.toISOString()
    : (overview.liveUntil ?? endOfMonth.toISOString());
  const deleteOn = projected
    ? new Date(endOfMonth.getTime() + 60 * 86_400_000).toISOString()
    : (overview.dataDeletedOn ??
      new Date(endOfMonth.getTime() + 60 * 86_400_000).toISOString());

  return (
    <ol className="timetable">
      <li>
        {/* Dayboard bills in ARREARS: the month you cancel in is invoiced at
            the end of it (spec 09, and `endOfCancellationMonth` says so in as
            many words). This line used to read "you have paid for this month",
            which was the opposite of true and promised a final invoice was not
            coming. The dates were always right; only the reason was wrong. */}
        <strong>Until {longDate(liveUntil)}</strong>: everything works normally. You get the whole
        month you are in, and it is invoiced at the end of it as usual.
      </li>
      <li>
        <strong>After that</strong>: read-only. Every job, document and record stays exactly where
        it is, and you can still look at all of it and export it.
      </li>
      <li>
        <strong>{longDate(deleteOn)}</strong>: the data is permanently deleted. We do not keep
        records that are not being used.
      </li>
    </ol>
  );
}

function StatusPanel({ overview }: { overview: BillingOverview }) {
  if (overview.status === 'trialing') {
    const daysLeft = Math.max(
      0,
      Math.ceil((new Date(overview.trialEndsAt).getTime() - Date.now()) / 86_400_000)
    );
    return (
      <Banner kind="ok">
        Trial: {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left, until{' '}
        {longDate(overview.trialEndsAt)}. Add a card to keep going.
      </Banner>
    );
  }

  if (overview.status === 'read_only') {
    return (
      <Banner kind="error">
        This account is read-only. Everything you put in is still here. Add a card below and it all
        wakes up.
      </Banner>
    );
  }

  if (overview.status === 'cancelled') {
    return (
      <Banner kind="error">
        Cancelled and read-only. You can still see and export everything
        {overview.dataDeletedOn ? ` until ${longDate(overview.dataDeletedOn)}` : ''}.
      </Banner>
    );
  }

  return null;
}

/**
 * Export. Spec 09's headline feature, so it gets its own panel rather than a
 * link in a menu — and it stays available on a frozen or cancelled account,
 * which is exactly when it matters.
 */
function ExportPanel({
  exports,
  busy,
  onRequest,
}: {
  exports: ExportOverview | null;
  busy: boolean;
  onRequest: () => void;
}) {
  const latest = exports?.exports[0];
  const building = exports?.inProgress ?? false;

  return (
    <section className="panel">
      <h2>Export everything</h2>
      <p>
        Every record as a spreadsheet, plus every document, in one zip. Yours to keep, whether you
        stay or go.
      </p>

      {building && (
        <p className="hint">
          Building your export now. It can take a few minutes for a big account; we will email you
          when it is ready, and you can close this page.
        </p>
      )}

      {!building && exports?.download && latest && (
        <p>
          <a href="/api/exports/download" className="button">
            Download the zip
          </a>{' '}
          <span className="hint">
            {latest.recordCount} records, {latest.fileCount} documents
            {latest.sizeBytes !== null ? `, ${fileSize(latest.sizeBytes)}` : ''}
            {latest.expiresAt ? `, available until ${longDate(latest.expiresAt)}` : ''}
          </span>
        </p>
      )}

      {!building && !exports?.download && latest?.status === 'expired' && (
        <p className="hint">
          Your last export, from {longDate(latest.requestedAt)}, has expired and been deleted. Ask
          for a fresh one any time.
        </p>
      )}

      {!building && latest?.status === 'failed' && (
        <Banner kind="error">
          The last export did not finish. Try again, and let us know if it keeps failing.
        </Banner>
      )}

      <button type="button" onClick={onRequest} disabled={busy || building}>
        {exports?.download ? 'Build a fresh export' : 'Export my data'}
      </button>
    </section>
  );
}
